import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Bookmark,
  ChevronDown,
  Download,
  Mail,
  Search,
  SlidersHorizontal,
  Trash2,
  UserCheck,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Card, ConfirmDialog, Input, Modal, Select, Spinner } from '@/components/ui';
import {
  NOT_SET_UP,
  createHrSavedView,
  deleteHrSavedView,
  exportHrPeople,
  listHrPeople,
  listHrSavedViews,
  listNotSetUpPeople,
  updateHrSavedView,
  type HrFacet,
  type HrPersonRow,
  type HrSavedViewFilters,
} from '@/api/services/hrWorkspace.service';
import { CompanyEmployeeProfile } from '@/features/companies/components/CompanyEmployeeProfile';
import { StartEmploymentModal } from '@/features/companies/components/StartEmploymentModal';
import { usePermissions } from '@/hooks/usePermissions';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { triggerBlobDownload } from '@/utils/download';
import { PERSON_STATUS_LABELS } from '../../hrAreas';
import type { HrAreaProps } from '../hrShared';
import { MessagePeopleDialog } from './MessagePeopleDialog';
import { SetUpPeopleDialog, type SetUpTarget } from './SetUpPeopleDialog';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = Object.entries(PERSON_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  not_set_up: 'warning',
  active: 'success',
  probation: 'info',
  preboarding: 'info',
  onboarding: 'info',
  on_leave: 'neutral',
  suspended: 'danger',
  notice: 'warning',
  exited: 'neutral',
  archived: 'neutral',
};

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

function personName(row: HrPersonRow): string {
  return row.employee
    ? `${row.employee.firstName} ${row.employee.lastName}`.trim()
    : 'Unknown employee';
}

const rowKey = (row: { companyId: string; userId: string }): string =>
  `${row.companyId}:${row.userId}`;

/** Facets belong to one company, so across companies the label says which. */
function facetOptions(
  facets: HrFacet[] | undefined,
  companyName: (companyId: string) => string | undefined,
  showCompany: boolean
): Array<{ value: string; label: string }> {
  return (facets ?? []).map((facet) => {
    const company = showCompany ? companyName(facet.companyId) : undefined;
    return { value: facet.id, label: company ? `${facet.name} · ${company}` : facet.name };
  });
}

interface PeopleListProps extends HrAreaProps {
  companyNameById: Map<string, string>;
  /** Switches the company filter when a saved view belongs to one company. */
  onApplyCompany: (companyId: string | null) => void;
}

/**
 * Everyone HR manages, across companies, in one list. People who have no
 * employment record yet appear as "Not set up" with a way to fix that.
 */
export function PeopleList({
  companyIds,
  showCompany,
  companyNameById,
  onApplyCompany,
}: PeopleListProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canExportOrMessage = can('hr', 'directory');
  const canSetUp = can('hr', 'employment');
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState(() => searchParams.get('status') ?? '');
  const [departmentId, setDepartmentId] = useState('');
  const [jobTitleId, setJobTitleId] = useState('');
  const [employmentTypeId, setEmploymentTypeId] = useState('');
  const [moreFilters, setMoreFilters] = useState(false);
  const [selected, setSelected] = useState<Map<string, HrPersonRow>>(new Map());
  const [setUpTargets, setSetUpTargets] = useState<SetUpTarget[] | null>(null);
  const [manualSetUp, setManualSetUp] = useState<SetUpTarget | null>(null);
  const [messaging, setMessaging] = useState(false);
  const [viewId, setViewId] = useState('');
  const [isSavingView, setIsSavingView] = useState(false);
  const [isDeletingView, setIsDeletingView] = useState(false);
  const [viewName, setViewName] = useState('');

  const clearSelection = (): void => setSelected(new Map());
  const resetPage = (): void => {
    setPage(1);
    clearSelection();
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
      setSelected(new Map());
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  // A different set of companies is a different list.
  const companyKey = companyIds?.join(',') ?? '';
  const [listedCompanies, setListedCompanies] = useState(companyKey);
  if (listedCompanies !== companyKey) {
    setListedCompanies(companyKey);
    setPage(1);
    setSelected(new Map());
  }

  const filters = useMemo(
    () => ({
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(status && { lifecycleStatus: status }),
      ...(departmentId && { departmentId }),
      ...(jobTitleId && { jobTitleId }),
      ...(employmentTypeId && { employmentTypeId }),
    }),
    [debouncedSearch, departmentId, employmentTypeId, jobTitleId, status]
  );
  const hasFilters = Object.keys(filters).length > 0;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['hr-employments', 'people', companyKey, page, filters],
    queryFn: () => listHrPeople(companyIds, page, PAGE_SIZE, filters),
    placeholderData: keepPreviousData,
  });
  const { data: savedViews = [] } = useQuery({
    queryKey: ['hr-people-views'],
    queryFn: listHrSavedViews,
  });

  const rows = Array.isArray(data?.data) ? data.data : [];
  const total = data?.total ?? 0;
  const notSetUpTotal = data?.notSetUpTotal ?? 0;
  const companyName = (id: string): string | undefined => companyNameById.get(id);

  const selectedRows = [...selected.values()];
  const selectedNotSetUp = selectedRows.filter((row) => row.lifecycleStatus === NOT_SET_UP);
  const selectedSetUp = selectedRows.filter((row) => row.lifecycleStatus !== NOT_SET_UP);
  const selectable = (row: HrPersonRow): boolean =>
    row.lifecycleStatus === NOT_SET_UP ? canSetUp : canExportOrMessage;
  const selectableRows = rows.filter(selectable);
  const isPageSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selected.has(rowKey(row)));

  const toggleRow = (row: HrPersonRow): void => {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(rowKey(row))) next.delete(rowKey(row));
      else next.set(rowKey(row), row);
      return next;
    });
  };
  const togglePage = (): void => {
    setSelected(
      isPageSelected
        ? new Map()
        : new Map(selectableRows.map((row) => [rowKey(row), row]))
    );
  };
  const asTarget = (row: HrPersonRow): SetUpTarget => ({
    companyId: row.companyId,
    userId: row.userId,
    name: personName(row),
  });

  const selectedEmployee = searchParams.get('employee');
  const selectedEmployeeCompany = searchParams.get('employeeCompany');
  const openProfile = (row: HrPersonRow | null): void => {
    const next = new URLSearchParams(searchParams);
    if (row) {
      next.set('employee', row.userId);
      next.set('employeeCompany', row.companyId);
    } else {
      next.delete('employee');
      next.delete('employeeCompany');
    }
    setSearchParams(next, { replace: true });
  };

  const setUpEveryone = useMutation({
    mutationFn: () => listNotSetUpPeople(companyIds, debouncedSearch || undefined),
    onSuccess: setSetUpTargets,
    onError: (mutationError) => toast.error(getErrorMessage(mutationError)),
  });
  const exportSelected = useMutation({
    mutationFn: () =>
      exportHrPeople(
        selectedSetUp.map(({ companyId, userId }) => ({ companyId, userId }))
      ),
    onSuccess: (blob) => {
      triggerBlobDownload(blob, `people-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success(`${selectedSetUp.length} records exported`);
    },
    onError: (mutationError) => toast.error(getErrorMessage(mutationError)),
  });

  const viewFilters = (): HrSavedViewFilters => ({
    ...(search.trim() && { search: search.trim() }),
    ...(status && { lifecycleStatus: status }),
    ...(departmentId && { departmentId }),
    ...(jobTitleId && { jobTitleId }),
    ...(employmentTypeId && { employmentTypeId }),
  });
  const refreshViews = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['hr-people-views'] });
  const saveView = useMutation({
    mutationFn: () =>
      createHrSavedView({
        name: viewName.trim(),
        ...(companyIds?.length === 1 && { companyId: companyIds[0] }),
        filters: viewFilters(),
      }),
    onSuccess: async (view) => {
      await refreshViews();
      setViewId(view._id);
      setIsSavingView(false);
      setViewName('');
      toast.success('View saved');
    },
    onError: (mutationError) => toast.error(getErrorMessage(mutationError)),
  });
  const updateView = useMutation({
    mutationFn: () => updateHrSavedView(viewId, { filters: viewFilters() }),
    onSuccess: async () => {
      await refreshViews();
      toast.success('View updated');
    },
    onError: (mutationError) => toast.error(getErrorMessage(mutationError)),
  });
  const deleteView = useMutation({
    mutationFn: () => deleteHrSavedView(viewId),
    onSuccess: async () => {
      await refreshViews();
      setViewId('');
      setIsDeletingView(false);
      toast.success('View deleted');
    },
    onError: (mutationError) => toast.error(getErrorMessage(mutationError)),
  });
  const applyView = (nextId: string): void => {
    setViewId(nextId);
    const view = savedViews.find((item) => item._id === nextId);
    if (!view) return;
    setSearch(view.filters.search ?? '');
    setDebouncedSearch(view.filters.search ?? '');
    setStatus(view.filters.lifecycleStatus ?? '');
    setDepartmentId(view.filters.departmentId ?? '');
    setJobTitleId(view.filters.jobTitleId ?? '');
    setEmploymentTypeId(view.filters.employmentTypeId ?? '');
    setMoreFilters(
      Boolean(
        view.filters.departmentId || view.filters.jobTitleId || view.filters.employmentTypeId
      )
    );
    onApplyCompany(view.companyId ?? null);
    resetPage();
  };
  const clearFilters = (): void => {
    setSearch('');
    setDebouncedSearch('');
    setStatus('');
    setDepartmentId('');
    setJobTitleId('');
    setEmploymentTypeId('');
    setViewId('');
    resetPage();
  };

  const moreFilterCount = [departmentId, jobTitleId, employmentTypeId].filter(Boolean).length;
  const statusIsNotSetUp = status === NOT_SET_UP;

  return (
    <>
      {canSetUp && notSetUpTotal > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-panel)] border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800/60 dark:bg-amber-950/30">
          <div className="min-w-0 max-w-2xl">
            <p className="text-sm font-medium text-amber-950 dark:text-amber-100">
              {notSetUpTotal} {notSetUpTotal === 1 ? 'person is' : 'people are'} not set up yet
            </p>
            <p className="mt-0.5 text-sm text-amber-900 dark:text-amber-200">
              They can&apos;t appear in the org chart, job changes, probation or letters until they
              have an employment record.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!statusIsNotSetUp && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setStatus(NOT_SET_UP);
                  resetPage();
                }}
              >
                Show them
              </Button>
            )}
            <Button
              size="sm"
              loading={setUpEveryone.isPending}
              onClick={() => setUpEveryone.mutate()}
            >
              <UserCheck className="mr-1.5 h-4 w-4" aria-hidden />
              Set up all {notSetUpTotal}
            </Button>
          </div>
        </div>
      )}

      <Card contentClassName="p-0">
        <div className="space-y-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700" aria-busy={isFetching}>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[12rem] flex-1 basis-64">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400"
                aria-hidden
              />
              <input
                id="people-search"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, email or ID"
                aria-label="Search people"
                className="box-border h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-primary/40 focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div className="w-full sm:w-44">
              <Select
                id="people-status"
                value={status}
                onChange={(value) => {
                  setStatus(value);
                  resetPage();
                }}
                placeholder="All statuses"
                triggerClassName="h-10"
                options={STATUS_OPTIONS}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              aria-expanded={moreFilters}
              aria-controls="people-more-filters"
              onClick={() => setMoreFilters((value) => !value)}
            >
              <SlidersHorizontal className="mr-1.5 h-4 w-4" aria-hidden />
              Filters{moreFilterCount > 0 ? ` · ${moreFilterCount}` : ''}
              <ChevronDown
                className={`ml-1 h-4 w-4 transition-transform ${moreFilters ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </Button>
            <div className="ml-auto flex items-center gap-2">
              {savedViews.length > 0 && (
                <div className="w-40">
                  <Select
                    id="people-saved-view"
                    value={viewId}
                    onChange={applyView}
                    placeholder="Saved views"
                    triggerClassName="h-10"
                    options={savedViews.map((view) => ({ value: view._id, label: view.name }))}
                  />
                </div>
              )}
              {viewId ? (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={updateView.isPending}
                    onClick={() => updateView.mutate()}
                  >
                    Update view
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Delete saved view"
                    onClick={() => setIsDeletingView(true)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </>
              ) : (
                hasFilters && (
                  <Button size="sm" variant="ghost" onClick={() => setIsSavingView(true)}>
                    <Bookmark className="mr-1.5 h-4 w-4" aria-hidden />
                    Save view
                  </Button>
                )
              )}
            </div>
          </div>
          {moreFilters && (
            <div id="people-more-filters" className="grid gap-3 sm:grid-cols-3">
              <Select
                id="people-department"
                label="Department"
                value={departmentId}
                onChange={(value) => {
                  setDepartmentId(value);
                  resetPage();
                }}
                placeholder="All departments"
                searchable
                options={facetOptions(data?.facets?.departments, companyName, showCompany)}
              />
              <Select
                id="people-job-title"
                label="Job title"
                value={jobTitleId}
                onChange={(value) => {
                  setJobTitleId(value);
                  resetPage();
                }}
                placeholder="All job titles"
                searchable
                options={facetOptions(data?.facets?.jobTitles, companyName, showCompany)}
              />
              <Select
                id="people-employment-type"
                label="Employment type"
                value={employmentTypeId}
                onChange={(value) => {
                  setEmploymentTypeId(value);
                  resetPage();
                }}
                placeholder="All employment types"
                options={facetOptions(data?.facets?.employmentTypes, companyName, showCompany)}
              />
            </div>
          )}
          {hasFilters && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-300" role="status">
                {total} {total === 1 ? 'match' : 'matches'}
                {isFetching ? ' · Updating…' : ''}
              </p>
              <Button size="sm" variant="ghost" onClick={clearFilters}>
                <X className="mr-1.5 h-4 w-4" aria-hidden />
                Clear filters
              </Button>
            </div>
          )}
        </div>

        {selectedRows.length > 0 && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-primary/5 px-4 py-2.5 dark:border-gray-700"
            role="region"
            aria-label="Actions for selected people"
          >
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {selectedRows.length} selected
            </p>
            <div className="flex flex-wrap gap-2">
              {canSetUp && selectedNotSetUp.length > 0 && (
                <Button size="sm" onClick={() => setSetUpTargets(selectedNotSetUp.map(asTarget))}>
                  <UserCheck className="mr-1.5 h-4 w-4" aria-hidden />
                  Set up {selectedNotSetUp.length}
                </Button>
              )}
              {canExportOrMessage && selectedSetUp.length > 0 && (
                <>
                  <Button size="sm" variant="secondary" onClick={() => setMessaging(true)}>
                    <Mail className="mr-1.5 h-4 w-4" aria-hidden />
                    Message {selectedSetUp.length}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={exportSelected.isPending}
                    onClick={() => exportSelected.mutate()}
                  >
                    <Download className="mr-1.5 h-4 w-4" aria-hidden />
                    Export {selectedSetUp.length}
                  </Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={clearSelection}>
                Clear
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center p-12" role="status">
            <Spinner />
            <span className="sr-only">Loading people</span>
          </div>
        ) : error ? (
          <div className="space-y-3 p-5" role="alert">
            <p className="text-sm text-danger">{getErrorMessage(error)}</p>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-gray-600 dark:text-gray-300">
            {hasFilters
              ? 'Nobody matches these filters.'
              : 'Nobody here yet. People appear once they are added to a company.'}
          </p>
        ) : (
          <table className="w-full table-fixed text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <tr>
                <th scope="col" className="w-11 py-2.5 pl-4">
                  {selectableRows.length > 0 && (
                    <input
                      type="checkbox"
                      aria-label="Select everyone on this page"
                      checked={isPageSelected}
                      onChange={togglePage}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  )}
                </th>
                <th scope="col" className="px-3 py-2.5">Name</th>
                {showCompany && (
                  <th scope="col" className="hidden w-[16%] px-3 py-2.5 md:table-cell">Company</th>
                )}
                <th scope="col" className="hidden px-3 py-2.5 lg:table-cell">Job</th>
                <th scope="col" className="w-32 px-3 py-2.5">Status</th>
                <th scope="col" className="hidden w-28 px-3 py-2.5 xl:table-cell">Joined</th>
                <th scope="col" className="w-24 px-3 py-2.5 sm:w-28">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((row) => {
                const notSetUp = row.lifecycleStatus === NOT_SET_UP;
                const name = personName(row);
                const job = row.currentAssignment;
                const isOpen =
                  selectedEmployee === row.userId && selectedEmployeeCompany === row.companyId;
                return (
                  <tr key={rowKey(row)} className={isOpen ? 'bg-primary/5' : undefined}>
                    <td className="py-2.5 pl-4">
                      {selectable(row) && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${name}`}
                          checked={selected.has(rowKey(row))}
                          onChange={() => toggleRow(row)}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      )}
                    </td>
                    <td className="min-w-0 px-3 py-2.5">
                      <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                        {name}
                      </span>
                      <span className="block truncate text-xs text-gray-600 dark:text-gray-300">
                        {row.employee?.employeeId ?? '—'}
                        {showCompany && row.companyName && (
                          <span className="md:hidden"> · {row.companyName}</span>
                        )}
                      </span>
                    </td>
                    {showCompany && (
                      <td className="hidden truncate px-3 py-2.5 text-gray-700 dark:text-gray-200 md:table-cell">
                        {row.companyName ?? '—'}
                      </td>
                    )}
                    <td className="hidden min-w-0 px-3 py-2.5 text-gray-700 dark:text-gray-200 lg:table-cell">
                      <span className="block truncate">
                        {job?.jobTitleName ?? (notSetUp ? '—' : 'No job title yet')}
                      </span>
                      {job?.departmentName && (
                        <span className="block truncate text-xs text-gray-600 dark:text-gray-300">
                          {job.departmentName}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge variant={STATUS_VARIANT[row.lifecycleStatus] ?? 'neutral'}>
                        {PERSON_STATUS_LABELS[row.lifecycleStatus] ?? row.lifecycleStatus}
                      </Badge>
                    </td>
                    <td className="hidden px-3 py-2.5 text-gray-700 dark:text-gray-200 xl:table-cell">
                      {formatDate(row.joinedAt)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {notSetUp ? (
                        canSetUp && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setSetUpTargets([asTarget(row)])}
                          >
                            Set up <span className="sr-only">{name}</span>
                          </Button>
                        )
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-expanded={isOpen}
                          onClick={() => openProfile(row)}
                        >
                          Open <span className="sr-only">{name}</span>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-4 py-2.5 dark:border-gray-700">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Page {page} of {Math.ceil(total / PAGE_SIZE)} · {total} people
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={page === 1}
                onClick={() => {
                  clearSelection();
                  setPage(page - 1);
                }}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={page * PAGE_SIZE >= total}
                onClick={() => {
                  clearSelection();
                  setPage(page + 1);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {selectedEmployee && selectedEmployeeCompany && (
        <CompanyEmployeeProfile
          key={`${selectedEmployeeCompany}:${selectedEmployee}`}
          companyId={selectedEmployeeCompany}
          userId={selectedEmployee}
          onClose={() => openProfile(null)}
        />
      )}
      {setUpTargets && (
        <SetUpPeopleDialog
          targets={setUpTargets}
          onEnterDetails={
            setUpTargets.length === 1
              ? () => {
                  setManualSetUp(setUpTargets[0]);
                  setSetUpTargets(null);
                }
              : undefined
          }
          onClose={() => {
            setSetUpTargets(null);
            clearSelection();
          }}
        />
      )}
      {manualSetUp && (
        <StartEmploymentModal
          companyId={manualSetUp.companyId}
          userId={manualSetUp.userId}
          employeeName={manualSetUp.name}
          onClose={() => setManualSetUp(null)}
        />
      )}
      {messaging && (
        <MessagePeopleDialog
          people={selectedSetUp.map(asTarget)}
          onClose={() => {
            setMessaging(false);
            clearSelection();
          }}
        />
      )}
      <Modal
        open={isSavingView}
        onClose={() => !saveView.isPending && setIsSavingView(false)}
        title="Save this view"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              disabled={saveView.isPending}
              onClick={() => setIsSavingView(false)}
            >
              Cancel
            </Button>
            <Button
              loading={saveView.isPending}
              disabled={!viewName.trim()}
              onClick={() => saveView.mutate()}
            >
              Save view
            </Button>
          </div>
        }
      >
        <Input
          id="people-view-name"
          label="View name"
          value={viewName}
          maxLength={60}
          required
          placeholder="e.g. On probation in Retail"
          onChange={(event) => setViewName(event.target.value)}
        />
      </Modal>
      <ConfirmDialog
        open={isDeletingView}
        onClose={() => !deleteView.isPending && setIsDeletingView(false)}
        onConfirm={() => deleteView.mutate()}
        title="Delete this saved view?"
        message="This only removes the saved filters. It does not change anyone's record."
        confirmLabel="Delete view"
        variant="danger"
        confirmLoading={deleteView.isPending}
      />
    </>
  );
}
